import Grid from '@mui/material/Grid';
import { Theme } from '@mui/material/styles';
import { useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { FixedSizeList } from 'react-window';
import { makeStyles } from 'tss-react/mui';
import { useGetGroupContacts, useGroupsList } from '../../../../sic-lib/api/hooks/useGroupsList';
import { ContactsSegmentsResponse } from '../../../../sic-lib/api/segments/types/ContactsSegmentsResponse';
import { hasMatchingListId } from '../../../../sic-lib/utils/functionalFilters';
import PageStatus from '../../../Navigation/PageStatus/PageStatus';
import GroupContactsHeader from '../GroupContactsHeader/GroupContactsHeader';
import GroupContactsListItem from '../GroupContactsListItem/GroupContactsListItem';
import GroupContactsNotFound from '../GroupContactsNotFound/GroupContactsNotFound';

interface Props {
    showListId: number;
}

export default function GroupContactsList({ showListId }: Props) {
    const { classes } = useStyles();
    const queryClient = useQueryClient();

    const listRef = React.useRef<HTMLDivElement | null>(null);
    const [listName, setListName] = React.useState<string>('');
    const [listHeight, setListHeight] = React.useState<number>(0);
    const { data: allGroupsData } = useGroupsList();
    const { data, isSuccess, isLoading, isError } = useGetGroupContacts(showListId);

    React.useEffect(() => {
        if (listRef.current) {
            const { height } = listRef.current.getBoundingClientRect();

            setListHeight(height);
        }
    }, []);

    //Effect used to determine if it is a group or segment and pull out it's name to display on
    //the group contact header and in case there is no contact to display on the group or segment
    React.useEffect(() => {
        if (showListId > 0 && allGroupsData) {
            const matchedList = allGroupsData.filter(hasMatchingListId(showListId))[0];

            if (matchedList) {
                setListName(matchedList.name);
            }
        } else if (showListId === -1) {
            setListName('All Contacts');
        } else {
            const queryData = queryClient
                .getQueryData<ContactsSegmentsResponse[]>(['segments'])
                ?.find(itm => itm.segmentId === showListId);

            if (queryData) {
                setListName(queryData.segmentName);
            }
        }

        return () => {
            setListName('');
        };
    }, [showListId, allGroupsData]);

    return (
        <Grid container direction="column" className={classes.root}>
            <Grid item className={classes.header}>
                <GroupContactsHeader listName={listName} />
            </Grid>
            <Grid item xs={true}>
                <div className="scrollWrapper--hideScrollbar--virtualList" ref={listRef}>
                    <PageStatus showError={isError} showLoading={!isSuccess || isLoading}>
                        {data && data.length > 0 ? (
                            <FixedSizeList
                                height={listHeight}
                                itemCount={data.length}
                                itemData={data || []}
                                itemSize={57}
                                width="100%"
                            >
                                {({ data, index, style }) => (
                                    <div style={style}>
                                        <GroupContactsListItem
                                            firstName={data[index].firstName}
                                            lastName={data[index].lastName}
                                            contactId={data[index].leadId}
                                            statusName={data[index].statusName}
                                        />
                                    </div>
                                )}
                            </FixedSizeList>
                        ) : (
                            <GroupContactsNotFound listName={listName} listId={showListId} />
                        )}
                    </PageStatus>
                </div>
            </Grid>
        </Grid>
    );
}

const useStyles = makeStyles()((theme: Theme) => ({
    root: {
        height: '100%',
    },
    header: {
        flexBasis: '56px',
        flexGrow: 0,
        padding: theme.spacing(1),
    },
}));
